import React, { useContext } from 'react';
import { Routes, useNavigate } from 'react-router-dom';
import {
	ArrowLeftStartOnRectangleIcon,
	BeakerIcon,
	CalendarDaysIcon,
	ChartPieIcon,
	CheckBadgeIcon,
	ClipboardDocumentCheckIcon,
	Cog6ToothIcon,
	DocumentIcon,
	DocumentMagnifyingGlassIcon,
	ExclamationTriangleIcon,
	HomeIcon,
	MapIcon,
	MapPinIcon,
	QueueListIcon,
	ServerIcon,
	ServerStackIcon,
	Squares2X2Icon,
	UserGroupIcon,
	UsersIcon,
} from '@heroicons/react/24/outline';

import AppContext from '../../context/AppContext';
import { SettingsProvider } from '../../context/SettingsContext';
import { SocketProvider } from '../../context/SocketContext';
import { ModalProvider } from '../../context/ModalContext';
import Shell from '../../components/Shell/Shell';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';
import UserMenu from '../../components/UserMenu';

import { checkRole } from '../../hooks/useRole';

// Dashboard Components
import Dashboard from './Dashboard';
import Consumables from '../Consumables';
import Assets from '../Assets';
import SingleAsset from '../Assets/Single';
import Map from '../Map';
import Calendar from '../Calendar';
import Tickets from '../Tickets';
import Contracts from '../Contracts';
import Directory from '../Directory';
import Documents from '../Documents';
import Locations from '../Locations';
import Settings from '../Settings';
import Tasks from '../Tasks';
import Team from '../Team';
import Insights from '../Insights';

import AddLocation from '../../modals/AddLocation';
import DeleteModal from '../../modals/DeleteModal';
import AddTicketSlider from '../../modals/AddTicketSlider';
import TicketOverview from '../../modals/TicketOverview';
import TaskDetailModal from '../../modals/TaskDetailModal';
import SopViewerModal from '../../modals/SopViewerModal';
import CreateTaskModal from '../../modals/CreateTaskModal';
import GlobalSearch from '../../modals/GlobalSearch';
import AddAssetSlider from '../../modals/AddAssetSlider';
import AddContactSlider from '../../modals/AddContactSlider';
import AddAttributes from '../../modals/AddAttributes';
import ManageAttributes from '../../modals/ManageAttributes';
import SelectLocationModal from '../../modals/SelectLocationModal';
import SelectContactsModal from '../../modals/SelectContactsModal';
import ViewProfileSlider from '../../modals/ViewProfileSlider';
import EventModal from '../../modals/EventModal';
import ServiceOverview from '../../modals/ServiceOverview';
import ViewContactModal from '../../modals/ViewContactModal';
import ContactModal from '../../modals/ContactModal';
import ContractOverview from '../../modals/ContractOverview';
import AddEventModal from '../../modals/AddEventModal';

import TabletView from '../../modals/TabletView';
import ManageColumns from '../../modals/ManageColumns';
import TableCSVExport from '../../modals/TableCSVExport';
import QuickAddVendor from '../../modals/QuickAddVendor';
import QuickAddCategory from '../../modals/QuickAddCategory';
import QuickAddTag from '../../modals/QuickAddTag';
import QuickAddConsumable from '../../modals/QuickAddConsumable';
import UploadDocuments from '../../modals/UploadDocuments';
import TaskCompletedModal from '../../modals/TaskCompletedModal';
import QuickAddPowerUsers from '../../modals/QuickAddPowerUsers';
import AddContractBetaModal from '../../modals/Contracts/AddContractBetaModal';
import AddTaskModal from '../../modals/Tasks/AddTaskModal';
import useSettings from '../../hooks/useSettings';
import BetaShell from './BetaShell';
import AddAssetModal from '../../modals/Assets/AddAssetModal';
import AddAssetModalEntry from '../../modals/Assets/AddAssetModalEntry';
import CustomFieldModal from '../../modals/CustomFieldModal';
import TicketOverviewTablet from '../../modals/TicketOverviewTablet';

const NotFound404 = ({ returnText, returnPath }) => {
	const navigate = useNavigate();
	return (
		<div className="text-center">
			<h3 className="mt-10 text-sm font-medium text-gray-900">404</h3>
			<p className="mt-1 text-sm text-gray-500">Page not found</p>
			{returnText && (
				<div className="mt-6">
					<button
						type="button"
						className="cursor-pointer inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
						onClick={() => navigate(returnPath)}
					>
						{returnText}
					</button>
				</div>
			)}
		</div>
	);
};

const getUserMenuNavigation = (user) => {
	return [
		...(checkRole(user, { name: 'default:superadmin' })
			? [{ name: 'Admin Panel', path: '/admin' }]
			: []),
		{ name: 'Sign Out', path: '/logout' },
	];
};

const getSidebarMenu = ({ flags }) => {
	return [
		{ name: 'Dashboard', icon: Squares2X2Icon, path: '/dashboard' },
		...(flags?.tasks
			? [
					{
						name: 'My Tasks',
						icon: CheckBadgeIcon,
						path: '/tasks',
					},
				]
			: []),
		...(flags?.calendar || true
			? [
					{
						name: 'Agenda',
						icon: CalendarDaysIcon,
						path: '/agenda',
					},
				]
			: []),
		{ name: 'Assets', icon: ServerIcon, path: '/assets/all' },
		...(flags?.consumables
			? [
					{
						name: 'Consumables',
						icon: BeakerIcon,
						path: '/consumables',
					},
				]
			: []),

		{
			name: 'Tickets',
			icon: ExclamationTriangleIcon,
			path: '/tickets',
		},

		...(flags?.map
			? [
					{
						name: 'Map',
						icon: MapIcon,
						path: '/map',
					},
				]
			: []),
		...(flags?.insights
			? [
					{
						name: 'Insights',
						icon: ChartPieIcon,
						path: '/insights',
					},
				]
			: []),
	];
};

const getSecondarySidebarMenu = () => {
	return [
		{ name: 'Contracts', path: '/manage/contracts', icon: DocumentIcon },
		{ name: 'Directory', path: '/manage/directory', icon: UsersIcon },
		{
			name: 'Documents',
			path: '/manage/documents',
			icon: DocumentMagnifyingGlassIcon,
		},
		{ name: 'Locations', path: '/manage/locations', icon: MapPinIcon },
		/*{
			name: 'Reminders',
			path: '/manage/reminders',
			icon: ClipboardDocumentCheckIcon,
		},*/
		{ name: 'Tasks', path: '/manage/tasks', icon: QueueListIcon },
		{ name: 'Team', path: '/manage/team', icon: UserGroupIcon },
	];
};

const DashboardRoutes = () => {
	const user = localStorage.getItem('user');

	const userJSON = user ? JSON.parse(user) : null;
	const hasConsumables =
		userJSON?.organization?.flags?.indexOf('consumables') > -1;
	const hasAppSettings =
		userJSON?.organization?.flags?.indexOf('appSettings') > -1;
	const hasInsights = userJSON?.organization?.flags?.indexOf('insights') > -1;

	const menu = <UserMenu navigation={getUserMenuNavigation(user)} />;

	const {
		get: getSettings,
		set: updateSettings,
		loading: isLoadingSettings,
	} = useSettings();

	return (
		<>
			<BetaShell
				UserMenu={menu}
				navigation={getSidebarMenu({
					flags: { consumables: hasConsumables, insights: hasInsights },
				})}
				secondaryNavigationTitle="Manage"
				secondaryNavigation={getSecondarySidebarMenu()}
				settingsNavigation={
					hasAppSettings
						? {
								name: 'App Settings',
								path: '/settings',
								icon: Cog6ToothIcon,
							}
						: undefined
				}
			>
				<Routes>
					{/*

						Dashboard

				*/}
					<ProtectedRoute
						exact
						path="/"
						forceRedirect={
							getSettings('userLandingPage')?.value === 'allAssets'
						}
						fallbackRedirect={'/assets/all'}
						component={Dashboard}
					/>
					<ProtectedRoute exact path="/dashboard" component={Dashboard} />
					{/* 

						Consumables 

				*/}
					<ProtectedRoute exact path="/consumables" component={Consumables} />
					<ProtectedRoute
						exact
						path="/consumables/:module"
						component={Consumables}
					/>
					<ProtectedRoute
						exact
						path="/consumables/:module/:action"
						component={Consumables}
					/>

					{/* 

						Assets

				*/}
					<ProtectedRoute
						exact
						path="/assets"
						forceRedirect
						fallbackRedirect={'/assets/all'}
					/>
					<ProtectedRoute exact path="/assets/all" component={Assets} />
					<ProtectedRoute exact path="/assets/all/issues" component={Assets} />
					<ProtectedRoute
						exact
						path="/assets/table/:tableId"
						component={Assets}
					/>
					<ProtectedRoute
						exact
						path="/assets/manufacturer/:manufacturerId"
						component={Assets}
					/>
					<ProtectedRoute
						exact
						path="/assets/model/:modelId"
						component={Assets}
					/>
					<ProtectedRoute
						exact
						path="/assets/category/:categoryId"
						component={Assets}
					/>
					<ProtectedRoute
						exact
						path="/assets/tags/:tagIds"
						component={Assets}
					/>
					<ProtectedRoute exact path="/assets/issues" component={Assets} />
					<ProtectedRoute exact path="/assets/add" component={Assets} />
					<ProtectedRoute exact path="/assets/new" component={Assets} />
					<ProtectedRoute
						exact
						path="/assets/location/:locationId"
						component={Assets}
					/>
					<ProtectedRoute exact path="/assets/:id/edit" component={Assets} />
					<ProtectedRoute exact path="/assets/:id" component={SingleAsset} />
					<ProtectedRoute
						exact
						path="/assets/:id/:tab"
						component={SingleAsset}
					/>
					<ProtectedRoute
						exact
						path="/assets/:id/:tab/:setting"
						component={SingleAsset}
					/>

					{/*

						Map (BETA)	

				*/}
					<ProtectedRoute exact path="/map" component={Map} />

					{/*

						Calendar

				*/}
					<ProtectedRoute exact path="/calendar" component={Calendar} />

					{/* 
				
						Tickets 
				
				*/}
					<ProtectedRoute exact path="/tickets" component={Tickets} />
					<ProtectedRoute exact path="/tickets/incoming" component={Tickets} />
					<ProtectedRoute exact path="/tickets/open" component={Tickets} />
					<ProtectedRoute
						exact
						path="/tickets/in-progress"
						component={Tickets}
					/>
					<ProtectedRoute exact path="/tickets/closed" component={Tickets} />
					<ProtectedRoute exact path="/tickets/create" component={Tickets} />
					<ProtectedRoute exact path="/tickets/:id" component={Tickets} />

					{/* 
				
						Manage Contracts
						
				*/}
					<ProtectedRoute path="/manage/contracts" component={Contracts} />

					{/*
				
						Manage Directory
						
				*/}
					<ProtectedRoute
						exact
						path="/manage/directory"
						component={Directory}
					/>
					<ProtectedRoute
						exact
						path="/manage/directory/new"
						component={Directory}
					/>
					<ProtectedRoute
						exact
						path="/manage/directory/:id"
						component={Directory}
					/>
					<ProtectedRoute
						exact
						path="/manage/directory/:id/edit"
						component={Directory}
					/>
					<ProtectedRoute
						exact
						path="/manage/directory/:id/assets"
						component={Directory}
					/>

					{/* 
				
						Documents 
						
				*/}
					<ProtectedRoute
						exact
						path="/manage/documents"
						component={Documents}
					/>

					{/* 
				
						Locations
						
				*/}
					<ProtectedRoute
						exact
						path="/manage/locations"
						component={Locations}
					/>

					{/* 
				
						Settings
						
				*/}
					<ProtectedRoute path="/settings" component={Settings} />
					{/*
					 
					
							Profile 
							
					*/}
					<ProtectedRoute exact path="/profile" component={Settings} />

					{/* 
						
							Tasks

					*/}
					<ProtectedRoute path="/manage/tasks" component={Tasks} />

					{/* 
					
							Team 
							
					*/}
					<ProtectedRoute exact path="/manage/team" component={Team} />
					<ProtectedRoute exact path="/manage/team/:tab" component={Team} />

					{/* 
					
							Insights 
							
					*/}
					<ProtectedRoute exact path="/insights" component={Insights} />

					<ProtectedRoute path={'*'} component={NotFound404} />
				</Routes>
			</BetaShell>

			{/* Locations Modal */}
			<AddLocation />

			{/* Delete Modal */}
			<DeleteModal />

			{/* Tickets Modals */}
			<AddTicketSlider />
			<TicketOverview />
			<TicketOverviewTablet />
			<TaskDetailModal />
			<SopViewerModal />
			<CreateTaskModal />

			{/* Global Search */}
			<GlobalSearch />

			{/* Add Asset */}
			<AddAssetSlider />

			{/* Add Contact */}
			<AddContactSlider />

			{/* Manage Attributes */}
			<AddAttributes />
			<ManageAttributes />

			{/* Select Location */}
			<SelectLocationModal />

			{/* Select Contacts */}
			<SelectContactsModal />

			<ViewProfileSlider />
			<EventModal />

			<ServiceOverview />
			<ViewContactModal />
			<ContactModal />
			<AddEventModal />
			<ContractOverview />

			<TabletView />

			<ManageColumns />
			<TableCSVExport />
			<QuickAddVendor />

			<QuickAddCategory />
			<QuickAddTag />
			<QuickAddConsumable />
			<UploadDocuments />

			<TaskCompletedModal />
			<QuickAddPowerUsers />

			<AddContractBetaModal />

			<AddAssetModal />
			<AddAssetModalEntry />

			<AddTaskModal />

			<CustomFieldModal />
		</>
	);
};

const DashboardEntry = () => {
	const { organization, ...rest } = useContext(AppContext);

	if (!organization?.active) {
		return <OrganizationNotActive />;
	}

	if (organization?.type === 'lab') {
		return (
			<ModalProvider>
				<SocketProvider>
					<SettingsProvider>
						<DashboardRoutes />
					</SettingsProvider>
				</SocketProvider>
			</ModalProvider>
		);
	}

	return <OrganizationNotFound />;
};

const OrganizationNotFound = () => {
	return <div>org not found</div>;
};

const OrganizationNotActive = () => {
	return <div>org not active</div>;
};

export default DashboardEntry;
